import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { Button, media, styled, RadioButton, Loader } from '@payright/web-components';
import { ButtonCancelPlan } from '../../components/buttons/ButtonCancelPlan';
import SCSpinner from '../../components/spinner';
import { ROUTES } from '../../routes';
import { FeatureFlags, useFeatures } from '../../providers/features-provider';

const Section = styled.section`
  p {
    font-size: 1.13em;
    margin-bottom: 2rem;
  }
  .title {
    color: ${(props) => props.theme.colours.blue.base};
    font-weight: bold;
    font-size: 1.125rem;
  }

  ${media.min.medium} {
    .title {
      font-size: 1.5rem;
    }
  }

  .buttons {
    display: flex;
    gap: 1.5rem;
    flex-direction: column-reverse;
  }

  ${media.min.medium} {
    .buttons {
      flex-direction: row;
    }
  }

  .loading-wrapper {
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const RadioGroup = styled.div`
  margin: 4.5rem 0;

  .radio-button {
    margin-bottom: 3rem;
    label {
      .option-name {
        padding-bottom: 0.5rem;
      }
      .subtext {
        font-weight: normal;
      }
    }
  }
`;

export const BankStatementsContent = () => {
  const globalCheckoutState = useSelector((state: RootState) => state.checkout);
  const loading = globalCheckoutState.loading;

  const history = useHistory();

  const {
    flagsConfig: { [FeatureFlags.PDF_BANK_STATEMENTS]: pdfBankStatementsFeatureFlag },
    isInitialised,
  } = useFeatures();

  const [bankStatementsOption, setBankStatementsOption] = useState<'instant' | 'manual-upload'>(
    'instant'
  );

  // If the PDF statements feature flag is not switched on, redirect to the original bank statements IFRAME page
  // Wait for Flagsmith to initialize before checking the flag to avoid redirecting too early
  useEffect(() => {
    if (isInitialised && !pdfBankStatementsFeatureFlag) {
      history.push(ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_ONLINE_BANKING + history.location.search);
    }
  }, [isInitialised, pdfBankStatementsFeatureFlag, history]);

  const handleSubmit = () => {
    history.push(
      bankStatementsOption === 'instant'
        ? ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_ONLINE_BANKING + history.location.search
        : ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_PDF_INSTRUCTIONS + history.location.search
    );
  };

  return (
    <Section>
      {!isInitialised ? (
        <div className="loading-wrapper">
          <Loader text="Loading..." />
        </div>
      ) : (
        <>
          <p className="title">Help us understand your financial situation</p>

          <p>To proceed with your application, we need to have a look at your bank statements.</p>

          <p>
            Choose how you'd like to share your bank statements. Pick the option that suits you.
          </p>

          <RadioGroup>
            <RadioButton
              name="bank-statements-option"
              value="instant"
              checked={bankStatementsOption}
              handleClick={() => setBankStatementsOption('instant')}
            >
              <div>
                <div className="option-name">Instant verification (recommended)</div>
                <div className="subtext">Securely connect using your online banking</div>
              </div>
            </RadioButton>
            <RadioButton
              name="bank-statements-option"
              value="manual-upload"
              checked={bankStatementsOption}
              handleClick={() => setBankStatementsOption('manual-upload')}
            >
              <div>
                <div className="option-name">Upload PDF statements</div>
                <div className="subtext">Select files from your computer or phone</div>
              </div>
            </RadioButton>
          </RadioGroup>

          <div className="buttons">
            <ButtonCancelPlan disabled={loading} />
            <Button
              handleClick={handleSubmit}
              disabled={loading}
              maxWidth="100%"
              icon={loading ? <SCSpinner /> : undefined}
            >
              Continue
            </Button>
          </div>
        </>
      )}
    </Section>
  );
};
